<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import InboxesAPI from 'dashboard/api/inboxes';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  inboxId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

const dialogRef = ref(null);
const contactCount = ref(0);
const isFetching = ref(true);
const isQueued = ref(false);

const hasDuplicates = computed(() => contactCount.value > 0);

const description = computed(() => {
  if (isQueued.value) {
    return t('INBOX_MGMT.MERGE_DUPLICATES.QUEUED');
  }
  if (isFetching.value) {
    return t('INBOX_MGMT.MERGE_DUPLICATES.COUNTING');
  }
  return hasDuplicates.value
    ? t('INBOX_MGMT.MERGE_DUPLICATES.FOUND', { count: contactCount.value })
    : t('INBOX_MGMT.MERGE_DUPLICATES.NONE');
});

const fetchCount = async () => {
  isFetching.value = true;
  try {
    const { data } = await InboxesAPI.getDuplicateConversations(props.inboxId);
    contactCount.value = data.contact_count;
  } catch (error) {
    contactCount.value = 0;
  } finally {
    isFetching.value = false;
  }
};

// The merge runs in a background job, so the count cannot be refreshed into a truthful value right
// after triggering it. The section switches to a queued state instead of showing a stale number.
const merge = async () => {
  try {
    await InboxesAPI.mergeDuplicateConversations(props.inboxId);
    isQueued.value = true;
    useAlert(t('INBOX_MGMT.MERGE_DUPLICATES.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.MERGE_DUPLICATES.ERROR_MESSAGE'));
  }
};

const handleDialogConfirm = async () => {
  await merge();
  dialogRef.value?.close();
};

onMounted(fetchCount);
</script>

<template>
  <SettingsFieldSection
    :label="t('INBOX_MGMT.MERGE_DUPLICATES.LABEL')"
    :help-text="t('INBOX_MGMT.MERGE_DUPLICATES.HELP_TEXT')"
  >
    <div class="flex items-center gap-3">
      <span class="text-body-main text-n-slate-11">{{ description }}</span>
      <NextButton
        slate
        :label="t('INBOX_MGMT.MERGE_DUPLICATES.BUTTON_TEXT')"
        :disabled="isFetching || !hasDuplicates || isQueued"
        @click="dialogRef?.open()"
      />
    </div>

    <Dialog
      ref="dialogRef"
      type="alert"
      :title="t('INBOX_MGMT.MERGE_DUPLICATES.CONFIRM.TITLE')"
      :description="
        t('INBOX_MGMT.MERGE_DUPLICATES.CONFIRM.DESCRIPTION', {
          count: contactCount,
        })
      "
      :confirm-button-label="t('INBOX_MGMT.MERGE_DUPLICATES.CONFIRM.YES')"
      :cancel-button-label="t('INBOX_MGMT.MERGE_DUPLICATES.CONFIRM.NO')"
      @confirm="handleDialogConfirm"
    />
  </SettingsFieldSection>
</template>
